import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  setAllEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  map,
  Observable,
  of,
  pipe,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { ConfigurationsConfigurationService } from './generated/configurations-configuration/configurations-configuration.service';
import type { ConfigurationV3 } from './generated/types/configurationV3';

interface AsyncFlags {
  loaded: boolean;
  error: string | null;
}

const SELECT_IDENTITY = { selectId: (c: ConfigurationV3) => c.identity };

export const ConfigurationStore = signalStore(
  withEntities<ConfigurationV3>(),
  withState<AsyncFlags>({ loaded: false, error: null }),
  withMethods(
    (store, api = inject(ConfigurationsConfigurationService)) => ({
      loadConfigs: rxMethod<void>(
        pipe(
          switchMap(() =>
            api.configurationsV3ConfigsList().pipe(
              tap((res) => {
                patchState(
                  store,
                  setAllEntities(res.configs, SELECT_IDENTITY),
                  { loaded: true, error: null },
                );
              }),
              catchError((err: { message?: string }) => {
                patchState(store, {
                  error: err.message ?? 'Error de carga',
                });
                return of(null);
              }),
            ),
          ),
        ),
      ),

      // Regular method returning Observable (pattern Rs485MasterStore.swap).
      // Component subscribes: `next` toasts success, error → global
      // httpErrorInterceptor already toasts.
      updateValue(input: {
        identity: string;
        value: string;
      }): Observable<ConfigurationV3> {
        return api
          .configurationsV3ConfigsPartialUpdate(
            { identity: input.identity },
            { value: input.value },
          )
          .pipe(
            tap((res) => {
              patchState(
                store,
                updateEntity(
                  { id: res.config.identity, changes: res.config },
                  SELECT_IDENTITY,
                ),
                { error: null },
              );
            }),
            catchError((err: { message?: string }) => {
              patchState(store, {
                error: err.message ?? 'Error al actualizar',
              });
              return throwError(() => err);
            }),
            map((res) => res.config),
          );
      },
    }),
  ),
);
